<template>
  <button
    :class="navItemClasses"
    @click="handleClick"
  >
    <NvxIcon
      :name="item.icon"
      :size="'lg'"
      class="dashboard-sidebar__nav-icon"
    />
    
    <NvxBadge 
      v-if="item.badge && !collapsed" 
      variant="secondary" 
      size="xs"
      class="dashboard-sidebar__nav-badge"
    >
      {{ item.badge }}
    </NvxBadge>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import NvxIcon from '../UI/NvxIcon.vue'
import NvxBadge from '../UI/NvxBadge.vue'

interface NavigationItem {
  id: string
  label: string
  icon: string
  href?: string
  badge?: string | number
}

interface Props {
  item: NavigationItem
  active?: boolean
  collapsed?: boolean
  role?: string
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
  collapsed: false,
  role: 'Admin'
})

const emit = defineEmits<{
  click: [item: NavigationItem]
}>()

const navItemClasses = computed(() => [
  'dashboard-sidebar__nav-link',
  {
    'dashboard-sidebar__nav-link--active': props.active,
    'dashboard-sidebar__nav-link--collapsed': props.collapsed,
    'dashboard-sidebar__nav-link--profile': props.active && props.role === 'Profile',
    'dashboard-sidebar__nav-link--editor': props.active && props.role === 'Editor',
    'dashboard-sidebar__nav-link--admin': props.active && props.role === 'Admin'
  }
])

const handleClick = (event: Event) => {
  // Always prevent default to avoid full page reload
  event.preventDefault()
  // Always emit click event - parent will handle navigation via Vue Router
  emit('click', props.item)
}
</script>

<style lang="scss" scoped>
.dashboard-sidebar__nav-link {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--nvx-spacing-3);
  padding: var(--nvx-spacing-3);
  background: none;
  border: none;
  border-radius: var(--nvx-border-radius-md);
  color: var(--nvx-color-text-secondary);
  text-decoration: none;
  font-size: var(--nvx-font-size-sm);
  font-weight: var(--nvx-font-weight-bold);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: var(--nvx-color-background-secondary);
    color: var(--nvx-color-text-primary);
  }

  &--active {
    color: var(--nvx-color-text-primary);
  }

  &--admin {
    &.dashboard-sidebar__nav-link--active {
      background-color: #04434A;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 20px;
        border-radius: 0 var(--nvx-border-radius-sm) var(--nvx-border-radius-sm) 0;
      }
    }
  }

  &--editor {
    &.dashboard-sidebar__nav-link--active {
      background-color: #5A30B7;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 20px;
        // background-color: #5A30B7;
        border-radius: 0 var(--nvx-border-radius-sm) var(--nvx-border-radius-sm) 0;
      }
    }
  }

  &--profile {
    &.dashboard-sidebar__nav-link--active {
      background-color: #27007A;
      

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 20px;
        // background-color: #5A30B7;
        border-radius: 0 var(--nvx-border-radius-sm) var(--nvx-border-radius-sm) 0;
      }
    }
  }

  &--collapsed {
    justify-content: center;
    padding: var(--nvx-spacing-3);

    &::before {
      display: none;
    }
  }
}

.dashboard-sidebar__nav-icon {
  flex-shrink: 0;
  color: white;
  font-size: 1.25vw;
}

.dashboard-sidebar__nav-label {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dashboard-sidebar__nav-badge {
  flex-shrink: 0;
  margin-left: auto;
}
</style>
